import { ErrorModal } from "@dexteel/mesf-core";
import {
	Box,
	CircularProgress,
	Divider,
	Paper,
	Typography,
} from "@mui/material";
import type { ExcelRow, GridApi } from "ag-grid-community";
import moment from "moment";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import { useStraightenerCanEdit } from "../../../production/straightener-common/permissions";
import { GroupsGrid } from "../../information-retrieval/components/GroupsGrid";
import { InformationRetrievalChart } from "../../information-retrieval/components/InformationRetrievalChart";
import { InformationRetrievalFilters } from "../../information-retrieval/components/InformationRetrievalFilters";
import { SummaryGrid } from "../../information-retrieval/components/SummaryGrid";
import type { ChartDisplayMode } from "../../information-retrieval/models/InformationRetrieval";
import {
	ALL_SHIFTS,
	filterToDrop,
	getFilterOptions,
} from "../../information-retrieval/utils/filterOptions";
import {
	useStraightenerInformationRetrievalData,
	useStraightenerInformationRetrievalPickers,
} from "../hooks/useStraightenerInformationRetrieval";
import {
	type RecipeProposalRequest,
	STRAIGHTENER_SUMMARY_PROPERTIES,
	type StraightenerInformationRetrievalFilters as Filters,
} from "../models/StraightenerInformationRetrieval";
import { BuildStraightenerRecipeModal } from "./BuildStraightenerRecipeModal";
import {
	buildChartSeries,
	buildGroups,
	buildRecipeLegend,
	buildRolls,
	buildSummary,
} from "../utils/aggregate";

const DATE_FORMAT = "YYYY-MM-DD";

const URL_SCHEMA = {
	// The legacy screen opens on today, not on a rolling window.
	start: urlParam.date(() => moment().startOf("day")),
	end: urlParam.date(() => moment().startOf("day")),
	productTypeId: urlParam.int(),
	productSizeId: urlParam.int(),
	shiftId: urlParam.string(ALL_SHIFTS),
	groupByDate: urlParam.bool(true),
	groupByShift: urlParam.bool(true),
};

const SERIES_NAMES = { target: "Recipe Start Point", actual: "Actual Break" };

const textCell = (value: string): ExcelRow["cells"][number] => ({
	data: { value, type: "String" },
});

export const StraightenerInformationRetrieval = () => {
	// The selected group rows are not kept: the grid selects them all on load and their
	// keys change completely with the range, so a link would carry a list that no longer
	// matches what came back.
	const [
		{
			start,
			end,
			productTypeId,
			productSizeId,
			shiftId,
			groupByDate,
			groupByShift,
		},
		setFilters,
		{ dropValues },
	] = useUrlState(URL_SCHEMA);

	const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
	const [displayMode, setDisplayMode] = useState<ChartDisplayMode>("average");
	const [rangeEnabled, setRangeEnabled] = useState(true);
	const [recipeRangeEnabled, setRecipeRangeEnabled] = useState(true);
	const [errorMsg, setErrorMsg] = useState("");
	const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
	const [notification, setNotification] = useState("");
	const summaryApiRef = useRef<GridApi | null>(null);
	const canEdit = useStraightenerCanEdit();

	const startStr = start.format(DATE_FORMAT);
	const endStr = end.format(DATE_FORMAT);

	const filters: Filters = useMemo(
		() => ({
			start: startStr,
			end: endStr,
			productTypeId,
			productSizeId,
			shiftId,
			groupByDate,
			groupByShift,
		}),
		[
			startStr,
			endStr,
			productTypeId,
			productSizeId,
			shiftId,
			groupByDate,
			groupByShift,
		],
	);

	const pickersQuery = useStraightenerInformationRetrievalPickers(
		startStr,
		endStr,
	);

	const selection = useMemo(
		() => ({ shiftId, productTypeId, productSizeId }),
		[shiftId, productTypeId, productSizeId],
	);
	const filterOptions = useMemo(
		() => getFilterOptions(pickersQuery.data, selection),
		[pickersQuery.data, selection],
	);
	const toDrop = pickersQuery.data
		? filterToDrop(pickersQuery.data, selection)
		: null;
	const selectionReady = pickersQuery.data != null && toDrop === null;

	// New dates can leave a selection that did not run together. Reset one
	// filter per pass; the next render re-checks what is left.
	useEffect(() => {
		if (toDrop === "product") dropValues("productSizeId");
		else if (toDrop === "productType") dropValues("productTypeId");
		else if (toDrop === "shift") dropValues("shiftId");
	}, [toDrop, dropValues]);

	const dataQuery = useStraightenerInformationRetrievalData(
		filters,
		selectionReady,
	);

	const rows = useMemo(() => dataQuery.data ?? [], [dataQuery.data]);
	const groups = useMemo(() => buildGroups(rows), [rows]);
	const rolls = useMemo(() => buildRolls(rows), [rows]);

	const summary = useMemo(
		() => buildSummary(groups, selectedKeys, rolls),
		[groups, selectedKeys, rolls],
	);
	const chartSeries = useMemo(
		() => buildChartSeries(groups, selectedKeys, rolls),
		[groups, selectedKeys, rolls],
	);
	const recipeLegend = useMemo(
		() => buildRecipeLegend(groups, selectedKeys),
		[groups, selectedKeys],
	);

	// With both groupings off every run collapses into one unlabelled row. The handlers
	// below keep that from happening on a click; this keeps it from happening on a link,
	// which arrives without passing through them.
	useEffect(() => {
		if (!groupByDate && !groupByShift) setFilters({ groupByDate: true });
	}, [groupByDate, groupByShift, setFilters]);

	const handleGroupByDate = useCallback(
		(value: boolean) => {
			// One write: two calls in the same handler would both read the same URL.
			setFilters({
				groupByDate: value,
				...(!value && !groupByShift ? { groupByShift: true } : {}),
			});
		},
		[groupByShift, setFilters],
	);

	const handleGroupByShift = useCallback(
		(value: boolean) => {
			setFilters({
				groupByShift: value,
				...(!value && !groupByDate ? { groupByDate: true } : {}),
			});
		},
		[groupByDate, setFilters],
	);

	// Back to the opening range, keeping the product when it ran today. A query
	// whose key does not change is refetched, so a reset on today pulls new runs.
	const handleReset = useCallback(() => {
		const today = moment().startOf("day");
		const datesUnchanged =
			start.isSame(today, "day") && end.isSame(today, "day");
		const dataKeyUnchanged =
			datesUnchanged && shiftId === ALL_SHIFTS && productTypeId == null;

		// The product stays, so it is not part of this write.
		setFilters({
			start: today,
			end: today,
			shiftId: ALL_SHIFTS,
			productTypeId: null,
		});

		if (datesUnchanged) pickersQuery.refetch();
		if (dataKeyUnchanged && selectionReady && productSizeId != null) {
			dataQuery.refetch();
		}
	}, [
		start,
		end,
		shiftId,
		productTypeId,
		productSizeId,
		selectionReady,
		pickersQuery,
		dataQuery,
	]);

	/** Same layout as the mill export: four rows per group, then the aggregate. */
	const handleExportToExcel = useCallback(() => {
		if (!summaryApiRef.current) return;

		const header: ExcelRow = {
			cells: [
				textCell("Date"),
				textCell("Break"),
				...rolls.map((r) => textCell(r)),
			],
		};

		const SERIES = [
			{ field: "avg", label: "Actual Break" },
			{ field: "target", label: "Recipe Start Point" },
			{ field: "min", label: "Min" },
			{ field: "max", label: "Max" },
		] as const;

		const groupRows: ExcelRow[] = groups.flatMap((group) =>
			SERIES.map(({ field, label }) => ({
				cells: [
					textCell(group.label),
					textCell(label),
					...rolls.map((roll) => {
						const cell = group.stands[roll];
						const value = cell?.active ? cell[field] : null;
						return textCell(value == null ? "" : value.toFixed(3));
					}),
				],
			})),
		);

		const summaryRows: ExcelRow[] = STRAIGHTENER_SUMMARY_PROPERTIES.map(
			(prop) => ({
				cells: [
					textCell(""),
					textCell(prop.label),
					...rolls.map((roll) => {
						const value = summary[prop.key][roll];
						if (value === null || value === undefined) return textCell("");
						if (prop.format === "PERCENT")
							return textCell(`${value.toFixed(0)}%`);
						return textCell(value.toFixed(prop.format === "FLOAT2" ? 2 : 3));
					}),
				],
			}),
		);

		summaryApiRef.current.exportDataAsExcel({
			fileName: "Straightener_Information_Retrieval.xlsx",
			shouldRowBeSkipped: () => true,
			skipColumnHeaders: true,
			prependContent: [
				header,
				...groupRows,
				{ cells: [] },
				{ cells: [textCell(""), textCell(`Recipe: ${recipeLegend}`)] },
				...summaryRows,
			],
		});
	}, [groups, rolls, summary, recipeLegend]);

	const error = dataQuery.error ?? pickersQuery.error;
	if (error && !errorMsg) {
		setErrorMsg((error as Error).message);
	}

	const hasData = groups.length > 0;
	const runsWithoutProduct = pickersQuery.data?.runsWithoutProduct ?? 0;

	// The proposal uses the same product, dates and shift as the report, and only
	// the groups left ticked.
	const recipeRequest = useMemo<RecipeProposalRequest | null>(() => {
		if (productSizeId == null) return null;
		return {
			start: startStr,
			end: endStr,
			productSizeId,
			shiftId,
			groups: groups
				.filter((g) => selectedKeys.includes(g.key))
				.map((g) => {
					const [date, shift] = g.key.split("|");
					return {
						date: date ? date.slice(0, 10) : null,
						shift: shift || null,
					};
				}),
		};
	}, [productSizeId, startStr, endStr, shiftId, groups, selectedKeys]);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1.5,
				p: 1.5,
				bgcolor: "#f0f2f5",
				minHeight: "calc(100vh - 130px)",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Straightener Information Retrieval
			</Typography>

			<Paper elevation={3}>
				<InformationRetrievalFilters
					start={start}
					end={end}
					onStartChange={(d) =>
						isUsableDate(d) && d && setFilters({ start: d })
					}
					onEndChange={(d) => isUsableDate(d) && d && setFilters({ end: d })}
					productTypeId={productTypeId}
					productSizeId={productSizeId}
					shiftId={shiftId}
					groupByDate={groupByDate}
					groupByShift={groupByShift}
					onProductTypeChange={(id) => setFilters({ productTypeId: id })}
					onProductSizeChange={(id) => setFilters({ productSizeId: id })}
					onShiftChange={(id) => setFilters({ shiftId: id })}
					onGroupByDateChange={handleGroupByDate}
					onGroupByShiftChange={handleGroupByShift}
					productTypes={filterOptions.productTypes}
					productSizes={filterOptions.productSizes}
					crews={filterOptions.crews}
					canUseCurrentShift={filterOptions.canUseCurrentShift}
					hasProduction={filterOptions.hasProduction}
					isLoadingOptions={pickersQuery.isFetching}
					noProductionText={
						runsWithoutProduct > 0
							? `The straightener ran ${runsWithoutProduct} times between {range}, but the PLC did not report the product.`
							: "Nothing ran on the straightener between {range}. Widen the date range."
					}
					onBuildRecipe={canEdit ? () => setIsRecipeModalOpen(true) : undefined}
					canBuildRecipe={hasData && selectedKeys.length > 0}
					buildRecipeHint="Build a recipe from the setpoints the ticked runs actually used"
					buildRecipeDisabledHint="Tick at least one group with runs"
					onReset={handleReset}
					onExportExcel={handleExportToExcel}
					isLoading={dataQuery.isFetching}
				/>
			</Paper>

			<Paper elevation={3} sx={{ p: 1.5, flex: 1 }}>
				{dataQuery.isInitialLoading || pickersQuery.isInitialLoading ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							minHeight: 320,
						}}
					>
						<CircularProgress />
					</Box>
				) : !hasData ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							minHeight: 320,
							color: mesfColors.text.secondary,
						}}
					>
						<Typography variant="h6">
							{!filterOptions.hasProduction
								? runsWithoutProduct > 0
									? `The straightener ran ${runsWithoutProduct} times in these dates, but the PLC did not report which product`
									: "Nothing ran on the straightener in the selected dates"
								: productSizeId == null
									? "Select a Product to load the report"
									: "No data available"}
						</Typography>
					</Box>
				) : (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<GroupsGrid
							groups={groups}
							stands={rolls}
							columnLabel="Roll"
							actualHint="Break the roll actually ran at"
							emptyCellHint="No reading for this roll in this group"
							onSelectionChange={setSelectedKeys}
							showLegendColor={displayMode === "straight"}
						/>

						<Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
							<Typography variant="body2" sx={{ fontWeight: 700 }}>
								Recipe:
							</Typography>
							<Typography variant="body2">{recipeLegend}</Typography>
						</Box>

						<SummaryGrid
							summary={summary}
							stands={rolls}
							properties={STRAIGHTENER_SUMMARY_PROPERTIES}
							columnLabel="Roll"
							onGridReady={(api) => {
								summaryApiRef.current = api;
							}}
						/>

						<Divider sx={{ my: 0.5 }} />

						<InformationRetrievalChart
							groups={groups}
							selectedKeys={selectedKeys}
							stands={rolls}
							aggregate={chartSeries}
							seriesNames={SERIES_NAMES}
							autoScaleY
							displayMode={displayMode}
							onDisplayModeChange={setDisplayMode}
							rangeEnabled={rangeEnabled}
							onRangeEnabledChange={setRangeEnabled}
							recipeRangeEnabled={recipeRangeEnabled}
							onRecipeRangeEnabledChange={setRecipeRangeEnabled}
						/>
					</Box>
				)}
			</Paper>

			<BuildStraightenerRecipeModal
				open={isRecipeModalOpen}
				request={recipeRequest}
				onClose={() => setIsRecipeModalOpen(false)}
				onSaved={() => {
					setIsRecipeModalOpen(false);
					setNotification(
						"Recipe saved as a new version. Find it in Configuration > Straightener > Recipes.",
					);
				}}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
